import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
gsap.registerPlugin( Draggable, InertiaPlugin );

export class Carousel {
	constructor( selector, options = {} ) {
		this.elements = document.querySelectorAll( selector );
		this.options = options;
		this.currentNum = 0;
		this.paginationDots = [];
		this.isEnable = false;
	}

	init() {
		if ( this.isEnable ) return;
		if ( ! this.elements?.length ) return;
		this.isEnable = true;
		this.elements.forEach( ( container ) => {
			const theWrap = container.querySelector( '.el-container' );
			if ( ! theWrap ) return;
			const thePagination = container.querySelector( '.el-pagination' );
			const theToggles = container.querySelector( '.el-toggle-group' );
			const slides = gsap.utils.toArray( '.el-item', theWrap );
			if ( slides.length < 2 ) {
				thePagination?.remove();
				theToggles?.remove();
				return;
			}
			const slideDelay = this.options.stay || 3;
			const slideDuration = this.options.duration || 0.5;
			const wrap = true;
			const progressWrap = gsap.utils.wrap( 0, 1 );
			const numSlides = slides.length;
			gsap.set( slides, {
				xPercent: ( i ) => i * 100,
			} );
			const wrapX = gsap.utils.wrap( -100, ( numSlides - 1 ) * 100 );

			const timer = gsap.delayedCall( slideDelay, autoPlay );
			const animation = gsap.to( slides, {
				xPercent: '+=' + numSlides * 100,
				duration: 1,
				ease: 'none',
				paused: true,
				repeat: -1,
				modifiers: {
					xPercent: wrapX,
				},
			} );
			const proxy = document.createElement( 'div' );
			let slideAnimation = gsap.to( {}, {} );
			const slideWidth = slides[ 0 ].offsetWidth;
			const wrapWidth = slideWidth * numSlides;

			const updateCurrent = ( index ) => {
				this.currentNum = index;
				this.paginationDots.forEach( ( dot, i ) => {
					dot.classList.toggle( 'current', i === index );
				} );
			};

			const updateProgress = () => {
				const currentProgress =
					gsap.getProperty( proxy, 'x' ) / wrapWidth;
				animation.progress( progressWrap( currentProgress ) );
				const currentSlideIndex =
					( Math.floor( currentProgress * numSlides ) * -1 ) %
					numSlides;
				if ( thePagination ) {
					updateCurrent( currentSlideIndex );
				}
			};

			const draggable = new Draggable( proxy, {
				type: 'x',
				trigger: container,
				inertia: true,
				onPress: updateDraggable,
				onDrag: updateProgress,
				onThrowUpdate: updateProgress,
				snap: {
					x: snapX,
				},
			} );

			if ( this.options.pauseOnHover ) {
				container.addEventListener( 'mouseenter', () => {
					timer.pause( true );
				} );
				container.addEventListener( 'mouseleave', () => {
					timer.restart( true );
				} );
			}

			const jumpTo = ( index ) => {
				const targetProgress = 1 - index / numSlides;
				const currentProgress = animation.progress();
				let adjustedTargetProgress = targetProgress;
				if ( currentProgress > targetProgress ) {
					adjustedTargetProgress =
						currentProgress - ( currentProgress - targetProgress );
				}
				if ( currentProgress !== adjustedTargetProgress ) {
					gsap.to( animation, {
						progress: adjustedTargetProgress,
						duration: slideDuration,
						ease: 'power2.inOut',
						onComplete: () => {
							updateCurrent( index );
						},
					} );
				}
				gsap.set( proxy, { x: index * slideWidth } );
			};

			if ( thePagination ) {
				this.paginationDots = Array.from(
					{ length: numSlides },
					( _, i ) => {
						const paginationDot = document.createElement( 'span' );
						if ( ! i ) {
							paginationDot.className =
								'el-pagination-dot current';
						} else {
							paginationDot.className = 'el-pagination-dot';
						}
						thePagination.appendChild( paginationDot );
						paginationDot.addEventListener( 'click', () =>
							jumpTo( i )
						);
						return paginationDot;
					}
				);
			}

			if ( theToggles ) {
				const thePrev = theToggles.querySelector( '.el-toggle-prev' );
				const theNext = theToggles.querySelector( '.el-toggle-next' );
				if ( thePrev ) {
					thePrev.addEventListener( 'click', () => {
						let toNum = this.currentNum - 1;
						if ( toNum < 0 ) toNum = slides.length - 1;
						jumpTo( toNum );
					} );
				}
				if ( theNext ) {
					theNext.addEventListener( 'click', () => {
						let toNum = this.currentNum + 1;
						if ( toNum > slides.length - 1 ) toNum = 0;
						jumpTo( toNum );
					} );
				}
			}

			function updateDraggable() {
				timer.restart( true );
				slideAnimation.kill();
				this.update();
			}

			function animateSlides( direction ) {
				timer.restart( true );
				slideAnimation.kill();
				const x = snapX(
					gsap.getProperty( proxy, 'x' ) + direction * slideWidth
				);

				slideAnimation = gsap.to( proxy, {
					x,
					duration: slideDuration,
					onUpdate: updateProgress,
				} );
			}

			function autoPlay() {
				if (
					draggable.isPressed ||
					draggable.isDragging ||
					draggable.isThrowing
				) {
					timer.restart( true );
				} else {
					animateSlides( -1 );
				}
			}

			function snapX( value ) {
				const snapped = gsap.utils.snap( slideWidth, value );
				return wrap
					? snapped
					: gsap.utils.clamp(
							-slideWidth * ( numSlides - 1 ),
							0,
							snapped
					  );
			}
		} );
	}

	kill() {}

	resize() {}

	destroy() {}
}
